import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, unwrap } from "@/lib/api";
import { qk } from "@/lib/query-client";
import { useAuth } from "@/hooks/useAuth";
import type {
  ApiEnvelope,
  InitiateSubscriptionRequest,
  InitiateSubscriptionResponse,
  ListMySubscriptionsResponse,
  VerifySubscriptionRequest,
  VerifySubscriptionResponse,
} from "@/lib/api-types";

/**
 * GET /subscriptions/me -> { active, history }
 */
export function useMySubscriptions() {
  const { status } = useAuth();
  return useQuery({
    queryKey: qk.subscriptions.mine(),
    enabled: status === "authenticated",
    queryFn: async () => {
      const res = await api.get<ApiEnvelope<ListMySubscriptionsResponse>>("/subscriptions/me");
      return unwrap(res.data) ?? { active: null, history: [] };
    },
    staleTime: 60 * 1000,
  });
}

/**
 * POST /subscriptions/initiate -> { razorpay_order_id, amount, ... }
 */
export function useInitiateSubscription() {
  return useMutation({
    mutationFn: async (req: InitiateSubscriptionRequest) => {
      const res = await api.post<ApiEnvelope<InitiateSubscriptionResponse>>(
        "/subscriptions/initiate",
        req,
      );
      const data = unwrap(res.data);
      if (!data) throw new Error("Empty initiate response");
      return data;
    },
  });
}

/**
 * POST /subscriptions/verify -> { subscription, idempotent? }
 */
export function useVerifySubscription() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (req: VerifySubscriptionRequest) => {
      const res = await api.post<ApiEnvelope<VerifySubscriptionResponse>>(
        "/subscriptions/verify",
        req,
      );
      const data = unwrap(res.data);
      if (!data) throw new Error("Empty verify response");
      return data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.subscriptions.mine() });
      void qc.invalidateQueries({ queryKey: qk.me });
    },
  });
}
